import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

/** Scroll-triggered fade/slide-in wrapper. */
export function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/** Simple hero header for inner pages. */
export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <section className="pt-32 pb-14 md:pt-40 md:pb-20 bg-gradient-to-b from-[#FFF9F5] to-[#FDFBF7]">
      <motion.div
        className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        {eyebrow && (
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EBE8E3] px-4 py-1.5 text-xs font-semibold text-[#4A4846]">
            {eyebrow}
          </div>
        )}
        <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1918] leading-[1.05]">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-5 text-lg md:text-xl text-[#4A4846] max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        )}
        {children && <div className="mt-8">{children}</div>}
      </motion.div>
    </section>
  );
}

export function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`py-16 md:py-20 ${className}`}>
      <motion.div
        className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </section>
  );
}

export function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [hash]);
}

export function CTALink({ to, children, variant = "primary", testId }) {
  const styles =
    variant === "primary"
      ? "bg-[#FF5A00] hover:bg-[#E65100] text-white shadow-lg shadow-orange-500/25"
      : "bg-white text-[#1A1918] border border-[#EBE8E3] hover:border-[#FF5A00]";
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-colors ${styles}`}
    >
      {children}
    </Link>
  );
}
